import { useState, type FormEvent } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import {
  ArrowLeft, Eye, EyeOff, Lock, AlertTriangle, CheckCircle2, KeyRound, ArrowRight,
} from "lucide-react"
import { ApiError, api } from "../../lib/api"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Label } from "../../components/ui/Label"
import { Alert } from "../../components/ui/Alert"
import { Card, CardContent } from "../../components/ui/Card"
import { ThemeToggle } from "../../components/ui/ThemeToggle"

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token") || ""

  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const noToken = !token

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!token) {
      setError("Reset token is missing. Please use the link from your email.")
      return
    }
    if (!password || !confirmPassword) {
      setError("Please fill in both password fields.")
      return
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.")
      return
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.")
      return
    }
    setLoading(true)
    try {
      await api.post("/api/auth/reset-password", { token, password })
      setDone(true)
      toast.success("Password reset successfully")
      setTimeout(() => navigate("/portal/login"), 1800)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError("Failed to reset password. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-ink-50 dark:bg-ink-950">
      {/* Top bar */}
      <header className="flex items-center justify-between p-4 md:p-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-600 dark:text-ink-300 hover:text-brand-700 dark:hover:text-brand-400 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to website
        </Link>
        <ThemeToggle />
      </header>

      <main className="flex-1 flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-md">
          <Card className="bg-white dark:bg-ink-900">
            <CardContent className="p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-brand-50 dark:bg-brand-900/40 grid place-items-center mx-auto mb-3">
                  {done ? (
                    <CheckCircle2 className="w-7 h-7 text-brand-700 dark:text-brand-400" />
                  ) : (
                    <KeyRound className="w-7 h-7 text-brand-700 dark:text-brand-400" />
                  )}
                </div>
                <h1 className="font-display text-2xl font-bold text-ink-900 dark:text-ink-100 mb-1">
                  {done ? "Password Reset" : "Reset Your Password"}
                </h1>
                <p className="text-sm text-ink-500 dark:text-ink-400">
                  {done
                    ? "Your password has been updated. Redirecting to login..."
                    : "Choose a new password for your account."}
                </p>
              </div>

              {noToken && (
                <Alert variant="warning" className="mb-4 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">
                    No reset token was found in the URL. Please open the reset link from your email
                    exactly as provided.
                  </span>
                </Alert>
              )}

              {error && (
                <Alert variant="danger" className="mb-4 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </Alert>
              )}

              {done ? (
                <div className="space-y-4">
                  <Alert variant="success" className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      Your password has been reset successfully. You can now log in with your new
                      password.
                    </span>
                  </Alert>
                  <Link to="/portal/login">
                    <Button size="lg" className="w-full">
                      Continue to Login
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  <div>
                    <Label htmlFor="password">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Min. 8 characters"
                        className="pl-9 pr-9 bg-white dark:bg-ink-900"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        disabled={loading || noToken}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(s => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600 dark:hover:text-ink-200"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                      <Input
                        id="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Re-enter your new password"
                        className="pl-9 bg-white dark:bg-ink-900"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        disabled={loading || noToken}
                        required
                      />
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1.5">Passwords do not match.</p>
                    )}
                  </div>

                  {/* Password requirements */}
                  <div className="rounded-lg border border-ink-200 dark:border-ink-800 bg-ink-50 dark:bg-ink-900/60 p-3">
                    <div className="text-xs font-semibold text-ink-700 dark:text-ink-300 mb-1.5">
                      Password requirements
                    </div>
                    <ul className="text-xs text-ink-500 dark:text-ink-400 space-y-0.5">
                      <li>• At least 8 characters</li>
                      <li>• Mix of letters, numbers and symbols recommended</li>
                      <li>• Avoid common words or your name/email</li>
                    </ul>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={loading || noToken}
                  >
                    {loading ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white" />
                        Resetting password...
                      </>
                    ) : (
                      <>
                        Reset Password
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}

              {!done && (
                <div className="mt-6 pt-4 border-t border-ink-200 dark:border-ink-800 text-center">
                  <p className="text-sm text-ink-600 dark:text-ink-400">
                    Remembered your password?{" "}
                    <Link to="/portal/login" className="font-semibold text-brand-700 dark:text-brand-400 hover:underline">
                      Back to Login
                    </Link>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  )
}
